import React, { useState, useEffect, useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, Users, Loader2, Radio, Maximize2, Minimize2, Copy, Check } from 'lucide-react';
import { getPublicOrigin } from '../lib/publicUrl';

/**
 * Modal "Spectateur QR" — affiche un GRAND QR à projeter sur le vidéoprojecteur
 * pour que les élèves scannent depuis leur banc et voient les slides en direct
 * sur leur téléphone (lecture seule).
 *
 * Props :
 *   - open (bool)
 *   - code (string | null) — code de la session déjà créée
 *   - onStart () — démarre une session
 *   - onClose ()
 *   - onStopCast ()
 *   - loading, error
 */
export default function SpectateurQrModal({ open, code, onStart, onClose, onStopCast, loading, error }) {
  const [projectorMode, setProjectorMode] = useState(false);
  const [copied, setCopied] = useState(false);
  const publicOrigin = getPublicOrigin();
  const viewerUrl = useMemo(() => (code ? `${publicOrigin}/present/live/${code}?viewer=1` : ''), [code, publicOrigin]);

  useEffect(() => {
    if (open && !code && !loading && onStart) onStart();
  }, [open, code, loading, onStart]);

  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(viewerUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };

  if (!open) return null;

  // Mode projecteur = QR géant plein écran pour affichage à la classe
  if (projectorMode && code) {
    return (
      <div className="fixed inset-0 z-[9999] bg-white flex flex-col items-center justify-center p-8" data-testid="spectateur-projector">
        <button
          onClick={() => setProjectorMode(false)}
          data-testid="spectateur-projector-exit"
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/80 text-white hover:bg-black flex items-center justify-center"
          aria-label="Quitter"
        >
          <Minimize2 size={18} />
        </button>
        <div className="text-black text-[13px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-4">
          Scannez pour suivre le cours sur votre téléphone
        </div>
        <div className="p-8 rounded-3xl bg-white shadow-2xl border-2 border-black/10">
          <QRCodeSVG value={viewerUrl} size={520} level="H" includeMargin={false} />
        </div>
        <div className="mt-6 text-center">
          <div className="text-black/50 text-[13px] font-mono-spec uppercase tracking-widest font-bold">Code à saisir sur</div>
          <div className="text-black text-[22px] font-serif-editorial">codevoiture.fr</div>
          <div className="font-mono-spec text-black text-[72px] font-black tracking-[0.15em] leading-none mt-2">{code}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9998] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="spectateur-modal">
      <div
        className="w-full max-w-lg rounded-3xl border border-emerald-500/40 bg-gradient-to-br from-[#0a1a10] to-[#0a0a0b] p-6 sm:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">
            <Users size={11} /> Spectateur QR élèves
          </div>
          <button onClick={onClose} data-testid="spectateur-modal-close" className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center">
            <X size={16} />
          </button>
        </div>

        {loading && !code && (
          <div className="py-14 text-center">
            <Loader2 size={30} className="animate-spin mx-auto text-emerald-400 mb-3" />
            <div className="text-white/60 text-[13px]">Création de la session…</div>
          </div>
        )}

        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-[13px] mb-4">{error}</div>
        )}

        {code && (
          <>
            <h2 className="font-serif-editorial text-white text-[24px] sm:text-[28px] leading-tight mb-2">
              Les élèves suivent depuis leur téléphone
            </h2>
            <p className="text-white/55 text-[13px] leading-relaxed mb-5">
              Projette ce QR sur le vidéoprojecteur pendant 30 secondes. Chaque élève scanne, et le cours s&apos;affiche en direct sur son téléphone (lecture seule, synchronisé à ce que tu montres).
            </p>

            {/* QR + code */}
            <div className="rounded-2xl bg-white p-5 flex items-center gap-5">
              <div className="shrink-0">
                <QRCodeSVG value={viewerUrl} size={140} level="M" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] uppercase tracking-widest text-black/50 font-bold font-mono-spec">Code élève</div>
                <div className="font-mono-spec text-[48px] font-black text-black leading-none tracking-[0.15em] my-1" data-testid="spectateur-code">{code}</div>
                <div className="text-black/60 text-[11px] leading-tight">
                  Scanner le QR<br />ou taper sur <strong>codevoiture.fr</strong>
                </div>
              </div>
            </div>

            {/* URL copyable */}
            <div className="mt-3 flex items-center gap-2">
              <div className="flex-1 min-w-0 h-10 px-3 rounded-lg bg-white/5 border border-white/10 flex items-center text-white/70 text-[12px] font-mono-spec truncate">
                {viewerUrl}
              </div>
              <button
                onClick={copyUrl}
                data-testid="spectateur-copy"
                className={`h-10 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 transition ${copied ? 'bg-emerald-500 text-emerald-950' : 'bg-white/10 hover:bg-white/15 text-white'}`}
              >
                {copied ? <><Check size={13} /> Copié</> : <><Copy size={13} /> Copier</>}
              </button>
            </div>

            {/* Actions */}
            <div className="mt-6 flex flex-wrap gap-2">
              <button
                onClick={() => setProjectorMode(true)}
                data-testid="spectateur-projector-mode"
                className="flex-1 h-12 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-[13.5px] font-bold inline-flex items-center justify-center gap-2 transition"
              >
                <Maximize2 size={14} /> Afficher le QR en GRAND pour la classe
              </button>
              <button
                onClick={onStopCast}
                data-testid="spectateur-modal-stop"
                title="Arrêter la diffusion aux élèves"
                className="h-12 px-4 rounded-full border border-white/15 text-white/70 hover:text-red-300 hover:border-red-500/50 text-[12px] font-semibold inline-flex items-center gap-1.5"
              >
                <Radio size={13} /> Arrêter
              </button>
            </div>

            <div className="mt-4 rounded-xl bg-sky-950/25 border border-sky-500/30 p-3 text-[12px] text-sky-200 leading-relaxed">
              <strong>💡 Astuce</strong> — les élèves peuvent lâcher leur téléphone dès qu&apos;ils ont compris. Utile pour les élèves malvoyants ou mal placés dans la salle.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
